import { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { Button } from './button';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Searchable dropdown of NRCS Conservation Practice Standards.
 *
 * Users can type by code (e.g. "528") or name ("prescribed grazing"). Selecting
 * a row auto-fills nrcs_code + practice_name on the parent form. Display
 * label always renders as "CODE — Practice Name" so users see the exact
 * shape the PDF will render.
 *
 * The bundled list is served by GET /api/nrcs-practices (source & pull date
 * live in the JSON's `_meta` block — see /app/backend/data/nrcs_practices.json).
 */
export function NrcsPracticePicker({ practices, code, name, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return practices;
    return practices.filter((p) =>
      p.code.toLowerCase().includes(q) || p.name.toLowerCase().includes(q)
    );
  }, [practices, query]);

  const label = code && name ? `${code} — ${name}` : code || name || 'Pick an NRCS practice…';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal input-farm"
          data-testid="nrcs-practice-picker"
        >
          <span className={cn('truncate', !code && 'text-muted-foreground')}>{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[--radix-popover-trigger-width] max-h-[420px]"
        align="start"
        data-testid="nrcs-practice-popover"
      >
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="h-4 w-4 shrink-0 opacity-50" />
            <CommandInput
              placeholder="Search by code (528) or name (prescribed grazing)…"
              value={query}
              onValueChange={setQuery}
              className="flex h-10 w-full rounded-md bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="nrcs-practice-search"
            />
          </div>
          <CommandList>
            <CommandEmpty>No matches. Try a shorter query.</CommandEmpty>
            <CommandGroup>
              {filtered.slice(0, 100).map((p) => (
                <CommandItem
                  key={p.code}
                  value={`${p.code} ${p.name}`}
                  onSelect={() => {
                    onSelect(p);
                    setOpen(false);
                    setQuery('');
                  }}
                  data-testid={`nrcs-option-${p.code}`}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      code === p.code ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="font-mono text-xs mr-2 text-primary">{p.code}</span>
                  <span className="flex-1 truncate">— {p.name}</span>
                  <span className="ml-2 text-[10px] uppercase text-muted-foreground">{p.unit}</span>
                </CommandItem>
              ))}
              {filtered.length > 100 && (
                <div className="px-3 py-1.5 text-xs text-muted-foreground">
                  Showing 100 of {filtered.length}. Narrow your search to see more.
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default NrcsPracticePicker;
